import { flip, offset, shift } from '@floating-ui/react-dom';
import { Navbar2Element } from '@typedream/core';
import { findNodePath, TNode, toDOMNode } from '@udecode/plate-core';
import { useInterpret, useSelector } from '@xstate/react';
import { useEffect, useState } from 'react';
import { Floating } from '../../../../components/Floating';
import { navbarContainerOptionsMachine } from '../../../../nodes/navbar/fsm/NavbarContainer.fsm';
import { changeNavbar, removeNavbar } from '../../../../nodes/navbar/transformers';
import { OverlayPortal } from '../../../../overlay/OverlayPortal';
import { useTPlateEditorRef } from '../../../../plate';
import NavbarAlignOptions from '../../../Navbar/NavbarAlignOptions';
import NavbarPositionOptions from '../../../Navbar/NavbarPositionOptions';
import { NavbarRemoveOption } from '../../../Navbar/NavbarRemoveOption';
import { NavbarStyleOptions } from '../../../Navbar/NavbarStyleOptions';
import { ToolbarNavbarMenuItemDropEvent } from '../../../../types';

export function NavbarContainerOverlayToolbar() {
  const editor = useTPlateEditorRef();
  const [navbar, setNavbar] = useState<Navbar2Element | null>(null);
  const [domNode, setDomNode] = useState<HTMLElement | null>(null);

  const fsm = useInterpret(navbarContainerOptionsMachine, {
    actions: {
      effectUpdateSlateNavbarNode: (context, event) => {
        const nodeReference = findNodePath(editor, navbar as TNode)!;
        if (event.type === 'NAVBAR.REMOVE.CONFIRM') {
          setNavbar(null);
          setDomNode(null);
          removeNavbar(editor, nodeReference);
          editor.emitter.emit('toolbar:navbar:delete-navbar');
        } else {
          changeNavbar(editor, nodeReference, context?.dataNavbar);
        }
      },
    },
  });

  const fsmState = useSelector(fsm, (state) => ({
    showMenu: state.hasTag('ui:show_menu'),
    showDeleteNavbarDialog: state.hasTag('ui:show_delete_navbar_dialog'),
    forceHideMenu: state.hasTag('ui:force_hide_menu'),
    uiIsShowingMenu: state.context.uiIsShowingMenu,
  }));

  const handler = (navbarFromEmit: { nav: Navbar2Element; state: boolean }) => {
    const { nav, state } = navbarFromEmit;
    if (state) {
      const navbarDOMNode = toDOMNode(editor, nav);
      fsm.send({ type: 'POINTER.NAVBAR.ENTER' });

      if (navbarDOMNode) {
        setDomNode(navbarDOMNode);
        setNavbar(nav);
        fsm.send({ type: 'MENU.OPEN', payload: { newNavbar: nav } });
      }
    } else {
      fsm.send({ type: 'POINTER.NAVBAR.LEAVE' });
    }
  };

  const onAddMenuHandler = () => {
    fsm.send({ type: 'MENU_ITEM.MIDDLE.ADD' });
  };

  const onAddEndMenuHandler = () => {
    fsm.send({ type: 'MENU_ITEM.END.ADD' });
  };

  const onInsideDropdownMenuhandler = ({ isInside }: { isInside: boolean }) => {
    if (isInside) {
      fsm.send({ type: 'POINTER.DROPDOWN_MENU.ENTER' });
    } else {
      fsm.send({ type: 'POINTER.DROPDOWN_MENU.LEAVE' });
    }
  };

  const handleDropMenuItem = (payload: ToolbarNavbarMenuItemDropEvent) => {
    fsm.send({ type: 'MENU_ITEM.DROP', payload });
  };

  useEffect(() => {
    editor.emitter.on('toolbar:toggle-navbar-container-menu', handler);
    editor.emitter.on('toolbar:add-navbar-menu-item', onAddMenuHandler);
    editor.emitter.on('toolbar:add-navbar-endmenu-item', onAddEndMenuHandler);
    editor.emitter.on(
      'toolbar:toggle-navbar-container-inside-dropdown',
      onInsideDropdownMenuhandler
    );
    editor.emitter.on('toolbar:navbar:menu-item:drop', handleDropMenuItem);

    return () => {
      editor.emitter.off('toolbar:toggle-navbar-container-menu', handler);
      editor.emitter.off('toolbar:add-navbar-menu-item', onAddMenuHandler);
      editor.emitter.off('toolbar:add-navbar-endmenu-item', onAddEndMenuHandler);
      editor.emitter.off(
        'toolbar:toggle-navbar-container-inside-dropdown',
        onInsideDropdownMenuhandler
      );
      editor.emitter.off('toolbar:navbar:menu-item:drop', handleDropMenuItem);
    };
  }, [editor.emitter]);

  useEffect(() => {
    if (!fsmState.uiIsShowingMenu) {
      fsm.send({ type: 'MENU.CLOSE' });
    }
  }, [fsmState.uiIsShowingMenu]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {
        /**
         * This overlay will be drawn in the Editor's canvas iframe
         */
        domNode &&
          (fsmState.showMenu || fsmState.showDeleteNavbarDialog) &&
          // condition above will be ignored when force_hide_menu is true
          !fsmState.forceHideMenu && (
            <OverlayPortal>
              <Floating
                reference={domNode}
                options={{
                  placement: 'bottom',
                  middleware: [
                    offset(-10),
                    flip(),
                    shift({
                      crossAxis: true,
                    }),
                  ],
                }}
                autoUpdate
                // style={{ zIndex: 2 }}
              >
                <div
                  onMouseEnter={() => {
                    fsm.send({ type: 'POINTER.MENU.ENTER' });
                  }}
                  onMouseLeave={() => {
                    fsm.send({ type: 'POINTER.MENU.LEAVE' });
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'stretch',
                    justifyContent: 'flex-start',
                    paddingTop: '5px',
                    paddingRight: '5px',
                    /* TODO(@ibedwi): add CSS Var z-index, previously z-index = 5 */
                    pointerEvents: 'auto',
                    padding: 10,
                    width: domNode.clientWidth,
                  }}
                >
                  <NavbarAlignOptions machine={fsm} />
                  <NavbarStyleOptions machine={fsm} />
                  <NavbarPositionOptions machine={fsm} />
                  <NavbarRemoveOption machine={fsm} />
                </div>
              </Floating>
            </OverlayPortal>
          )
      }
    </>
  );
}
